import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { formatDualDate, parseIsoDate, toIsoDate, today } from '@/shared/lib/dates';
import {
  coverageStatusColor,
  coverageStatusLabel,
  prayerLabel,
  roleLabel,
} from '@/shared/lib/labels';
import { COVERAGE_STATUSES, USER_ROLES, type UserRole } from '@/shared/model/enums';
import { Async } from '@/shared/ui/Async';
import { BarChart, BarList, Donut, LineChart, type LineChartSeries } from '@/shared/ui/charts';
import { useUiStore } from '@/stores/ui.store';

import {
  useAnalyticsSummary,
  useRequestsByCity,
  useRequestsBySlot,
  useRequestsTimeseries,
  useSignupsTimeseries,
  useSupportTimeseries,
} from '../hooks/use-analytics';
import { formatBucketTick, formatCount, formatDecimal, pivotSignups } from '../lib/insights-format';
import { ANALYTICS_GRANULARITIES, type AnalyticsGranularity } from '../model/analytics';

function defaultFrom(): string {
  const d = today();
  d.setDate(d.getDate() - 90);
  return toIsoDate(d);
}

interface KpiProps {
  readonly label: string;
  readonly value: string;
}

function Kpi({ label, value }: KpiProps): JSX.Element {
  return (
    <div className="stat-card stat-card--static">
      <div className="stat-card__value">{value}</div>
      <div className="stat-card__label">{label}</div>
    </div>
  );
}

/** Distinct token colors for the three legacy roles (no hardcoded hex). */
const ROLE_COLOR: Record<UserRole, string> = {
  imam: 'var(--color-primary)',
  muezzin: 'var(--color-accent-text)',
  masjid_admin: 'var(--color-on-surface-muted)',
};

/**
 * Phase 9: the Product-Manager "Insights" analytics dashboard
 * (`/admin/insights`). Backed entirely by the SQL-aggregating,
 * admin-gated `analytics_*` RPCs — no client-side counting of raw rows.
 * Four sections: coverage-request demand, user growth, marketplace
 * liquidity, and support load, plus a headline KPI row driven by
 * `analytics_summary`. The granularity + date-range control drives every
 * time-series query via the React Query key (see `use-analytics.ts`).
 */
export function AdminInsightsRoute(): JSX.Element {
  const { t } = useTranslation();
  const locale = useUiStore((s) => s.locale);

  const [granularity, setGranularity] = useState<AnalyticsGranularity>('week');
  const [from, setFrom] = useState<string>(defaultFrom);
  const [to, setTo] = useState<string>(() => toIsoDate(today()));

  const range = useMemo(() => ({ granularity, from, to }), [granularity, from, to]);
  const granularityLabel = t(`admin.insights.granularity_${granularity}`);

  const summary = useAnalyticsSummary();
  const requestsTimeseries = useRequestsTimeseries(range);
  const requestsByCity = useRequestsByCity();
  const requestsBySlot = useRequestsBySlot();
  const signupsTimeseries = useSignupsTimeseries(range);
  const supportTimeseries = useSupportTimeseries(range);

  return (
    <main className="page stack">
      <h1>{t('admin.insights.title')}</h1>
      <p className="muted">{t('admin.insights.subtitle')}</p>

      <Async query={summary}>
        {(s) => (
          <div className="stat-grid">
            <Kpi
              label={t('admin.insights.statTotalRequests')}
              value={formatCount(s.totalRequests, locale)}
            />
            <Kpi
              label={t('admin.insights.statOpenRequests')}
              value={formatCount(s.openRequests, locale)}
            />
            <Kpi
              label={t('admin.insights.statFillRate')}
              value={t('admin.insights.donutCenterLabel', { pct: Math.round(s.fillRate * 100) })}
            />
            <Kpi
              label={t('admin.insights.statAvgApplicants')}
              value={formatDecimal(s.avgApplicantsPerRequest, locale)}
            />
            <Kpi
              label={t('admin.insights.statAvgTimeToFill')}
              value={formatDecimal(s.avgTimeToFillHours, locale)}
            />
            <Kpi
              label={t('admin.insights.statNewSignups30d')}
              value={formatCount(s.newSignups30d, locale)}
            />
            <Kpi
              label={t('admin.insights.statOpenTicketBacklog')}
              value={formatCount(s.openTicketBacklog, locale)}
            />
            <Kpi
              label={t('admin.insights.statNewTickets30d')}
              value={formatCount(s.newTickets30d, locale)}
            />
          </div>
        )}
      </Async>

      <div className="insights-controls">
        <div className="insights-controls__field">
          <label htmlFor="insights-granularity">{t('admin.insights.granularityLabel')}</label>
          <select
            id="insights-granularity"
            value={granularity}
            onChange={(e) => setGranularity(e.target.value as AnalyticsGranularity)}
          >
            {ANALYTICS_GRANULARITIES.map((g) => (
              <option key={g} value={g}>
                {t(`admin.insights.granularity_${g}`)}
              </option>
            ))}
          </select>
        </div>
        <div className="insights-controls__field">
          <label htmlFor="insights-from">{t('admin.insights.dateFrom')}</label>
          <input
            id="insights-from"
            type="date"
            dir="ltr"
            className="ltr-field"
            value={from}
            max={to}
            onChange={(e) => setFrom(e.target.value)}
          />
        </div>
        <div className="insights-controls__field">
          <label htmlFor="insights-to">{t('admin.insights.dateTo')}</label>
          <input
            id="insights-to"
            type="date"
            dir="ltr"
            className="ltr-field"
            value={to}
            min={from}
            max={toIsoDate(today())}
            onChange={(e) => setTo(e.target.value)}
          />
        </div>
      </div>

      <section className="insights-section">
        <h2 className="insights-section__title">{t('admin.insights.sectionDemand')}</h2>
        <div className="insights-section__grid">
          <div className="insights-card insights-card--wide">
            <span className="insights-card__title">
              {t('admin.insights.chartRequestsOverTime')}
            </span>
            <Async query={requestsTimeseries} isEmpty={(d) => d.length === 0}>
              {(points) => {
                const labels = points.map((p) => formatBucketTick(p.bucket, granularity, locale));
                const fullLabels = points.map((p) =>
                  formatDualDate(parseIsoDate(p.bucket), locale),
                );
                const series: LineChartSeries[] = COVERAGE_STATUSES.map((status) => ({
                  id: status,
                  label: coverageStatusLabel(t, status),
                  color: coverageStatusColor(status),
                  values: points.map((p) => {
                    switch (status) {
                      case 'open':
                        return p.openCount;
                      case 'filled':
                        return p.filledCount;
                      case 'cancelled':
                        return p.cancelledCount;
                      case 'expired':
                        return p.expiredCount;
                    }
                  }),
                }));
                return (
                  <LineChart
                    labels={labels}
                    fullLabels={fullLabels}
                    series={series}
                    ariaLabel={t('admin.insights.ariaRequestsOverTime', {
                      granularity: granularityLabel,
                      from: formatDualDate(parseIsoDate(from), locale),
                      to: formatDualDate(parseIsoDate(to), locale),
                    })}
                  />
                );
              }}
            </Async>
          </div>

          <div className="insights-card">
            <span className="insights-card__title">{t('admin.insights.chartRequestsByCity')}</span>
            <Async query={requestsByCity} isEmpty={(d) => d.length === 0}>
              {(rows) => (
                <BarList
                  ariaLabel={t('admin.insights.ariaRequestsByCity')}
                  valueFormatter={(v) => formatCount(v, locale)}
                  data={rows.map((r) => ({
                    id: r.city ?? '__unknown__',
                    label: r.city ?? t('admin.insights.unknownCity'),
                    value: r.total,
                  }))}
                />
              )}
            </Async>
          </div>

          <div className="insights-card">
            <span className="insights-card__title">{t('admin.insights.chartRequestsBySlot')}</span>
            <Async query={requestsBySlot} isEmpty={(d) => d.length === 0}>
              {(rows) => (
                <BarChart
                  ariaLabel={t('admin.insights.ariaRequestsBySlot')}
                  valueFormatter={(v) => formatCount(v, locale)}
                  data={rows.map((r) => ({
                    id: r.slot,
                    label: prayerLabel(t, r.slot),
                    value: r.count,
                  }))}
                />
              )}
            </Async>
          </div>
        </div>
      </section>

      <section className="insights-section">
        <h2 className="insights-section__title">{t('admin.insights.sectionGrowth')}</h2>
        <div className="insights-section__grid">
          <div className="insights-card">
            <span className="insights-card__title">{t('admin.insights.chartSignupsByRole')}</span>
            <Async query={signupsTimeseries} isEmpty={(d) => d.length === 0}>
              {(points) => {
                const pivoted = pivotSignups(points);
                const labels = pivoted.buckets.map((b) => formatBucketTick(b, granularity, locale));
                const fullLabels = pivoted.buckets.map((b) =>
                  formatDualDate(parseIsoDate(b), locale),
                );
                const series: LineChartSeries[] = USER_ROLES.map((role) => ({
                  id: role,
                  label: roleLabel(t, role),
                  color: ROLE_COLOR[role],
                  values: pivoted.byRole[role],
                }));
                return (
                  <LineChart
                    labels={labels}
                    fullLabels={fullLabels}
                    series={series}
                    ariaLabel={t('admin.insights.ariaSignupsByRole', {
                      granularity: granularityLabel,
                    })}
                  />
                );
              }}
            </Async>
          </div>

          <div className="insights-card">
            <span className="insights-card__title">{t('admin.insights.chartSignupsByMode')}</span>
            <Async query={signupsTimeseries} isEmpty={(d) => d.length === 0}>
              {(points) => {
                const pivoted = pivotSignups(points);
                const labels = pivoted.buckets.map((b) => formatBucketTick(b, granularity, locale));
                const fullLabels = pivoted.buckets.map((b) =>
                  formatDualDate(parseIsoDate(b), locale),
                );
                const series: LineChartSeries[] = [
                  {
                    id: 'freelance',
                    label: t('nav.mode_freelance'),
                    color: 'var(--color-primary)',
                    values: pivoted.freelance,
                  },
                  {
                    id: 'appointed',
                    label: t('nav.mode_appointed'),
                    color: 'var(--color-accent-text)',
                    values: pivoted.appointed,
                  },
                ];
                return (
                  <LineChart
                    labels={labels}
                    fullLabels={fullLabels}
                    series={series}
                    ariaLabel={t('admin.insights.ariaSignupsByMode', {
                      granularity: granularityLabel,
                    })}
                  />
                );
              }}
            </Async>
          </div>
        </div>
      </section>

      <section className="insights-section">
        <h2 className="insights-section__title">{t('admin.insights.sectionLiquidity')}</h2>
        <div className="insights-section__grid">
          <div className="insights-card insights-card__gauge">
            <span className="insights-card__title">{t('admin.insights.chartFillRateGauge')}</span>
            <Async query={summary}>
              {(s) => (
                <Donut
                  value={s.fillRate}
                  centerLabel={t('admin.insights.donutCenterLabel', {
                    pct: Math.round(s.fillRate * 100),
                  })}
                  ariaLabel={t('admin.insights.ariaFillRateGauge', {
                    pct: Math.round(s.fillRate * 100),
                  })}
                />
              )}
            </Async>
          </div>
          <div className="insights-card">
            <span className="insights-card__title">{t('admin.insights.statAvgApplicants')}</span>
            <Async query={summary}>
              {(s) => (
                <p className="insights-card__bigNumber">
                  {formatDecimal(s.avgApplicantsPerRequest, locale)}
                </p>
              )}
            </Async>
          </div>
          <div className="insights-card">
            <span className="insights-card__title">{t('admin.insights.statAvgTimeToFill')}</span>
            <Async query={summary}>
              {(s) => (
                <p className="insights-card__bigNumber">
                  {formatDecimal(s.avgTimeToFillHours, locale)}
                </p>
              )}
            </Async>
          </div>
        </div>
      </section>

      <section className="insights-section">
        <h2 className="insights-section__title">{t('admin.insights.sectionSupport')}</h2>
        <div className="insights-card insights-card--wide">
          <span className="insights-card__title">{t('admin.insights.chartSupportOverTime')}</span>
          <Async query={supportTimeseries} isEmpty={(d) => d.length === 0}>
            {(points) => (
              <LineChart
                labels={points.map((p) => formatBucketTick(p.bucket, granularity, locale))}
                fullLabels={points.map((p) => formatDualDate(parseIsoDate(p.bucket), locale))}
                series={[
                  {
                    id: 'tickets',
                    label: t('admin.insights.seriesNewTickets'),
                    color: 'var(--color-primary)',
                    values: points.map((p) => p.count),
                  },
                ]}
                ariaLabel={t('admin.insights.ariaSupportOverTime', {
                  granularity: granularityLabel,
                })}
              />
            )}
          </Async>
        </div>
      </section>
    </main>
  );
}
